import { PayloadAction } from '@reduxjs/toolkit';
import { captureException } from '@sentry/browser';
import app from 'Cloud/Application/app';
import config from 'Cloud/config';
import { logger } from 'lib/logger';
import { RenameAlbumAPICal } from 'reactApp/api/albums/RenameAlbumAPICal';
import { RenameAPICall } from 'reactApp/api/RenameApiCall';
import { EAlbumAction, sendAlbumAnalytics } from 'reactApp/modules/albums/albums.analytics';
import { changeHomeHistory } from 'reactApp/modules/home/home.actions';
import { getCurrentFolderHome } from 'reactApp/modules/home/home.selectors';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { resetSelected } from 'reactApp/modules/selections/selection.saga';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { store as reduxStore } from 'reactApp/store';
import { put, select } from 'redux-saga/effects';

import { renameFileRequest, renameItemSuccess } from '../modifying.actions';
import { renameErrors } from '../modifying.constants';
import { getNewItem } from '../modifying.helpers';
import { IRenameItem } from '../modifying.types';

const MAX_NAME_LENGTH = config.get('ITEM_NAME_MAX_LENGTH');

const renameApiCall = ({ id, name, conflict = 'rename' }) => new RenameAPICall().makeRequest({ home: id, name, conflict });
const renameAlbumApiCall = ({ id, title }) => new RenameAlbumAPICal().makeRequest({ album_id: id, title });

export function renameRequest(item, newName) {
    return new Promise((resolve, reject) => {
        const nameWithoutExtension = newName.replace(/\.\w+$/i, '');
        if (!item?.id || (!nameWithoutExtension && !item?.isFolder) || !newName) {
            // @ts-ignore
            return reject(renameErrors.getMessage('required'));
        }

        if (newName.length > MAX_NAME_LENGTH) {
            return reject(renameErrors.getMessage('name_too_long'));
        }

        if (item.storage === EStorageType.albums) {
            sendAlbumAnalytics({ action: EAlbumAction.RENAME });

            renameAlbumApiCall({ id: item.id, title: newName })
                .then(({ data }) => reduxStore.dispatch(renameFileRequest({ item, newId: data?.title })))
                .catch(({ error }) => reject(renameErrors.getMessage(error)));

            return;
        }

        renameApiCall({ id: item.id, name: newName })
            .then(({ data }) => reduxStore.dispatch(renameFileRequest({ item, newId: data?.body })))
            .catch(({ response }) => reject(renameErrors.getMessage(response?.body)));
    });
}

function* processRename(item: CloudItem, newId: string) {
    const storage: EStorageType = yield select(getCurrentStorage);
    const newItem: CloudItem = getNewItem({ item, newId });

    try {
        // Добавляем в старое дерево
        yield app?.addOldItems?.([{ ...newItem }], newItem.parent, storage);
    } catch (_) {}

    // Если переименовали текущую папку, то открываем родителя
    if (storage === EStorageType.home) {
        const currentFolder = yield select(getCurrentFolderHome);
        const parentId = currentFolder?.parent;

        if (item.id === currentFolder?.id && parentId) {
            yield put(changeHomeHistory({ id: parentId }));
        }
    }

    yield put(renameItemSuccess({ oldId: item.id, newItem }));
    yield resetSelected();
    yield closePopupHelper(popupNames.RENAME_DIALOG);
}

export function* handleRenameFile(action: PayloadAction<IRenameItem>) {
    const { item, newId } = action.payload;

    try {
        yield processRename(item, newId);
    } catch (error) {
        yield closePopupHelper(popupNames.RENAME_DIALOG);
        logger.error(error);
        captureException(error);
    }
}
